import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { adminAPI } from '../../../services/api';
import { format } from 'date-fns';
import { FiSearch, FiFilter, FiRefreshCw } from 'react-icons/fi';
import { LuEye } from 'react-icons/lu';
import { Link } from 'react-router-dom';
import LoadingSpinner from '../../../components/LoadingSpinner';
import ErrorAlert from '../../../components/ErrorAlert';
import InventoryStylePagination from '../../../components/InventoryStylePagination';

const InvoicesList = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    status: '',
    customerId: '',
    dateRange: 'all'
  });
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);

  // Fetch invoices with filters
  const { data, isLoading, isError, error, refetch } = useQuery(
    ['admin-invoices', page, limit, searchTerm, filters],
    () => adminAPI.getInvoices({
      page,
      limit,
      search: searchTerm,
      ...filters
    }),
    {
      keepPreviousData: true
    }
  );

  // Fetch customers for filter
  const { data: customersData } = useQuery(
    'customers-minimal',
    () => adminAPI.getCustomers({ minimal: true }),
    {
      staleTime: 5 * 60 * 1000 // 5 minutes
    }
  );

  // Handle status filter change
  const handleStatusFilterChange = (e) => {
    setFilters({
      ...filters,
      status: e.target.value
    });
    setPage(1);
  };

  // Handle customer filter change
  const handleCustomerFilterChange = (e) => {
    setFilters({
      ...filters,
      customerId: e.target.value
    });
    setPage(1);
  };

  // Handle date range filter change
  const handleDateRangeFilterChange = (e) => {
    setFilters({
      ...filters,
      dateRange: e.target.value
    });
    setPage(1);
  };

  // Handle search
  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
  };

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      status: '',
      customerId: '',
      dateRange: 'all'
    });
    setSearchTerm('');
    setPage(1);
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (isError) {
    return <ErrorAlert message={error?.message || 'Error loading invoices'} />;
  }

  const invoices = data?.data || [];
  const customers = customersData?.customers || customersData?.data || [];

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8 w-full">
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-xl font-semibold text-gray-900">Invoices</h1>
          <p className="mt-2 text-sm text-gray-700">
            A list of all invoices including their status, amount, and due date.
          </p>
        </div>
      </div>

      {/* Search and filters */}
      <div className="mt-6 mb-8">
        <div className="mb-4">
          <form onSubmit={handleSearch} className="relative rounded-md shadow-sm max-w-md">
            <input
              type="text"
              className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pr-10 sm:text-sm border-gray-300 rounded-md"
              placeholder="Search by invoice number or customer name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <div className="absolute inset-y-0 right-0 flex items-center pr-3">
              <FiSearch className="h-4 w-4 text-gray-400" />
            </div>
          </form>
        </div>

        <div className="flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-3 sm:gap-4">
          <div className="flex items-center">
            <FiFilter className="mr-2 text-gray-500 hidden sm:block" />
            <label htmlFor="status-filter" className="sr-only">Filter by status</label>
            <select
              id="status-filter"
              value={filters.status}
              onChange={handleStatusFilterChange}
              className="focus:ring-indigo-500 focus:border-indigo-500 rounded-md border-gray-300 py-2 pl-3 pr-10 text-sm"
            >
              <option value="">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="sent">Sent</option>
              <option value="paid">Paid</option>
              <option value="overdue">Overdue</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
          
          <div className="flex items-center">
            <label htmlFor="customer-filter" className="sr-only">Filter by customer</label>
            <select
              id="customer-filter"
              value={filters.customerId}
              onChange={handleCustomerFilterChange}
              className="focus:ring-indigo-500 focus:border-indigo-500 rounded-md border-gray-300 py-2 pl-3 pr-10 text-sm"
            >
              <option value="">All Customers</option>
              {customers.map(customer => (
                <option key={customer.id} value={customer.id}>
                  {customer.companyName || customer.name}
                </option>
              ))}
            </select>
          </div>
          
          <div className="flex items-center">
            <label htmlFor="date-filter" className="sr-only">Filter by date</label>
            <select
              id="date-filter"
              value={filters.dateRange}
              onChange={handleDateRangeFilterChange}
              className="focus:ring-indigo-500 focus:border-indigo-500 rounded-md border-gray-300 py-2 pl-3 pr-10 text-sm"
            >
              <option value="all">All Time</option>
              <option value="today">Today</option>
              <option value="week">This Week</option>
              <option value="month">This Month</option>
              <option value="year">This Year</option>
            </select>
          </div>
          
          <div className="flex space-x-2">
            <button
              type="button"
              onClick={clearFilters}
              className="inline-flex items-center px-2.5 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Clear
            </button>
            
            <button
              type="button"
              onClick={() => refetch()}
              className="inline-flex items-center px-2.5 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              <FiRefreshCw className="mr-1 h-3 w-3" /> Refresh
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white shadow overflow-hidden sm:rounded-md mt-4">
        {invoices.length === 0 ? (
          <div className="px-4 py-5 sm:p-6 text-center">
            <p className="text-gray-500 text-base">No invoices found.</p>
            <p className="text-gray-400 text-sm mt-1">Try adjusting your filters or search terms.</p>
          </div>
        ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-300">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">Invoice Number</th>
                <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Customer</th>
                <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Amount</th>
                <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Status</th>
                <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Issue Date</th>
                <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Due Date</th>
                <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {invoices.map((invoice) => (
                <tr key={invoice.id} className="hover:bg-gray-50">
                  <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium sm:pl-6">
                    <Link to={`/admin/invoices/${invoice.id}`} className="text-indigo-600 hover:text-indigo-900">
                      {invoice.invoiceNumber}
                    </Link>
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                    {invoice.Company?.name || 'Unknown Customer'}
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                    ${parseFloat(invoice.totalAmount || 0).toFixed(2)}
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      invoice.status === 'paid' ? 'bg-green-100 text-green-800' :
                      invoice.status === 'overdue' ? 'bg-red-100 text-red-800' :
                      invoice.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
                    </span>
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                    {format(new Date(invoice.issueDate), 'MMM d, yyyy')}
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                    {format(new Date(invoice.dueDate), 'MMM d, yyyy')}
                  </td>
                  <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                    <Link to={`/admin/invoices/${invoice.id}`} className="text-gray-400 hover:text-gray-600" aria-label="View invoice">
                      <LuEye className="h-4 w-4" />
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        )}

        <InventoryStylePagination
          currentPage={page}
          totalPages={data?.totalPages || 1}
          totalItems={data?.total || 0}
          pageSize={limit}
          onPageChange={setPage}
          onPageSizeChange={(nextLimit) => {
            setLimit(nextLimit);
            setPage(1);
          }}
        />
      </div>
    </div>
  );
};

export default InvoicesList; 
